import { _ } from "../../../../../core/polyglot/polyglot.js";
import { select } from "../../../../../core/elements/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import RadioGroupWidget from "../../../../../core/widgets/RadioGroupWidget/RadioGroupWidget.js";

const masterVisibilityValue = "master-visibility";

/**
 * Widget used to set the visibility of a single item (row or column).
 *
 * @param {AbstractItem} spec.item - The template for which the visibility is set.
 * @param {Report} spec.report - Report from which all items are fetched to
 * select a master visibility item.
 * @param {string[]} [spec.visibilityOptions] - Defines which visibility settings
 * are available. Defaults to all of them.
 */
export default class VisibilitySettingsWidget extends Widget {
	constructor({ item, report, visibilityOptions }) {
		super(...arguments);

		this._item = item;
		this._report = report;
		this._visibilityOptions = visibilityOptions;

		this._radioGroup = this._makeRadioGroup();
	}

	reset() {
		this._radioGroup = this._makeRadioGroup();
		this.update();
	}

	updateVisibility() {
		this._radioGroup.getSelected().action();
	}

	renderContentOn(html) {
		html.div(
			{ klass: "setting-section" },
			this._radioGroup,
			this._renderMasterVisibilityDropdownOn.bind(this),
		);

		html.div(
			{ klass: "setting-section" },
			html.div(
				{ klass: "description-text" },
				this._radioGroup.getSelected().description,
			),
		);
	}

	_renderMasterVisibilityDropdownOn(html) {
		if (this._getSelectedValue() !== masterVisibilityValue) return;
		html.render(this._makeMasterVisibilityDropdown());
	}

	_getSelectedValue() {
		return this._radioGroup.getSelected().value;
	}

	_allElements() {
		return [
			{
				value: "always-visible",
				label: _("Always visible"),
				description: this._item.isRow()
					? _("The row will always be displayed in the report.")
					: _("The column will always be displayed in the report."),
				action: () => {
					this._item.setHidden(false);
					this._item.setOptional(false);
					this._item.setVisibleWhenNoData(true);
					this._item.setMasterVisibilityTemplateId(null);
				},
			},
			{
				value: "visible-if-data",
				label: _("Visible if data"),
				description: this._item.isRow()
					? _("The row will be displayed if it contains data.")
					: _("The column will be displayed if it contains data."),
				action: () => {
					this._item.setHidden(false);
					this._item.setOptional(false);
					this._item.setVisibleWhenNoData(false);
					this._item.setMasterVisibilityTemplateId(null);
				},
			},
			{
				value: "always-hidden",
				label: _("Always hidden"),
				description: this._item.isRow()
					? _(
							"The row will not be displayed in the report. You can still use it in the editor, e.g., as a formula component.",
						)
					: _(
							"The column will not be displayed in the report. You can still use it in the editor, e.g., as a formula component.",
						),
				action: () => {
					this._item.setHidden(true);
					this._item.setOptional(false);
					this._item.setVisibleWhenNoData(false);
					this._item.setMasterVisibilityTemplateId(null);
				},
			},
			{
				value: "optional-visible",
				label: _("Optional, visible by default"),
				description: this._item.isRow()
					? _(
							"The row will be visible by default but can be hidden by the end-user.",
						)
					: _(
							"The column will be visible by default but can be hidden by the end-user.",
						),
				action: () => {
					this._item.setHidden(false);
					this._item.setOptional(true);
					this._item.setVisibleWhenNoData(false);
					this._item.setMasterVisibilityTemplateId(null);
				},
			},
			{
				value: "optional-hidden",
				label: _("Optional, hidden by default"),
				description: this._item.isRow()
					? _(
							"The row will be hidden by default but can be made visible by the end-user.",
						)
					: _(
							"The column will be hidden by default but can be made visible by the end-user.",
						),
				action: () => {
					this._item.setHidden(true);
					this._item.setOptional(true);
					this._item.setVisibleWhenNoData(false);
					this._item.setMasterVisibilityTemplateId(null);
				},
			},
			{
				value: masterVisibilityValue,
				label: this._item.isRow()
					? _("Same as another row")
					: _("Same as another column"),
				description: this._item.isRow()
					? _(
							"The row will be displayed if and only if the other row is displayed in the report.",
						)
					: _(
							"The column will be displayed if and only if the other column is displayed in the report.",
						),
				action: () => {
					this._item.setHidden(false);
					this._item.setOptional(false);
					this._item.setVisibleWhenNoData(false);

					if (this._selectedMasterVisibility) {
						this._item.setMasterVisibilityTemplateId(
							this._selectedMasterVisibility
								.getTemplate()
								.getId(),
						);
					}
				},
			},
		];
	}

	_makeRadioGroup() {
		const radioGroup = new RadioGroupWidget({
			elements: this._visibleElements(),
		});

		radioGroup.selectValue(this._item.getVisibilitySettingId());
		radioGroup.selectionChanged.register(() => this.update());

		return radioGroup;
	}

	_visibleElements() {
		return this._allElements().filter((item) =>
			this._isVisible(item.value),
		);
	}

	_isVisible(elementValue) {
		return (
			!this._visibilityOptions ||
			this._visibilityOptions.includes(elementValue)
		);
	}

	_makeMasterVisibilityDropdown() {
		const predecessors = this._item.getPredecessors();

		/**
		 * For visibility, we cannot use all items:
		 *
		 * - An item cannot depend on itself
		 * - An item cannot depend on its predecessors, because predecessors already depend on their children for visibility
		 * - An item can only depend on referenceable items
		 *
		 */
		const allItems = this._item
			.getAllSimilarItems({ report: this._report })
			.filter(
				(item) =>
					item.getTemplate() !== this._item &&
					!predecessors.some(
						(element) => element === item.getTemplate(),
					) &&
					item.canBeReferenced(),
			);

		this._selectedMasterVisibility = allItems.find(
			(item) =>
				item.getTemplate().getId() ===
				this._item.getMasterVisibilityTemplateId(),
		);

		return select({
			items: allItems.map((item) => ({
				label: item.getTitle(),
				value: item,
			})),
			name: "master-item",
			selected: this._selectedMasterVisibility,
			onChange: (value) => (this._selectedMasterVisibility = value),
		});
	}
}
